import { createClient } from '@/lib/supabase/client';

export interface SubscriptionPlan {
  id: string;
  name: string;
  plan_type: 'monthly' | 'yearly';
  price: number;
  stripe_price_id: string | null;
  features: string[];
  is_active: boolean;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  status: 'active' | 'canceled' | 'past_due' | 'trialing';
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface PaymentSettings {
  payment_required: boolean;
}

export const subscriptionService = {
  // Get all subscription plans
  async getPlans(): Promise<SubscriptionPlan[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('price', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // Get user's current subscription
  async getUserSubscription(userId: string): Promise<UserSubscription | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_subscriptions').select('*').eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  },

  // Check if payment is required (override setting)
  async isPaymentRequired(): Promise<boolean> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('payment_settings').select('setting_value').eq('setting_key', 'payment_required')
      .single();

    if (error) return false;
    return data?.setting_value === 'true';
  },

  // Check if user has active subscription
  async hasActiveSubscription(userId: string): Promise<boolean> {
    const subscription = await this.getUserSubscription(userId);
    if (!subscription) return false;

    const isActive = subscription.status === 'active';
    const notExpired = !subscription.current_period_end ||
      new Date(subscription.current_period_end) > new Date();

    return isActive && notExpired;
  },

  // Check if user can access platform (either payment not required OR has active subscription)
  async canAccessPlatform(userId: string): Promise<boolean> {
    const paymentRequired = await this.isPaymentRequired();

    // If payment not required, allow access
    if (!paymentRequired) return true;

    // If payment required, check subscription
    return await this.hasActiveSubscription(userId);
  },

  // Create checkout session
  async createCheckoutSession(planId: string, userId: string): Promise<{ url: string }> {
    const supabase = createClient();

    const { data, error } = await supabase.functions.invoke<{ url: string }>('create-checkout-session', {
      body: { planId, userId }
    });

    if (error) {
      console.error('Checkout session creation failed:', error, data);
      const errorMessage = (data as any)?.error || error.message || 'Failed to create checkout session';
      throw new Error(errorMessage);
    }

    if (!data?.url) {
      throw new Error('No checkout URL returned');
    }

    return data;
  },

  // Cancel subscription
  async cancelSubscription(userId: string): Promise<void> {
    const supabase = createClient();
    const { error } = await supabase
      .from('user_subscriptions')
      .update({
        cancel_at_period_end: true,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) throw error;
  }
};